<template>
  <v-app>
    <v-expand-transition>
      <v-app-bar
        v-if="!notReady"
        id="mainNav"
        app
        :height="appbarHeight"
        class="white"
      >
        <v-container class="d-flex align-center">
          <div class="d-flex align-center">
            <img
              alt="Vuetify Logo"
              class="shrink mr-2 ml-6"
              contain
              src="@/assets/nctwcs/logo.png"
              transition="scale-transition"
              :width="logoWidth"
            />
          </div>
          <h1
            class="font-weight-bold primary--text flex-fill flex-fill text-center pl-4 text-truncate"
            :class="{ 'text-h4': !isMobile, 'text-h6': isMobile }"
          >
            {{ title }}
          </h1>
        </v-container>
      </v-app-bar>
    </v-expand-transition>
    <v-main class="grey lighten-2">
      <v-fade-transition>
        <div v-if="!notReady">
          <v-container class="pa-4 pa-md-8">
            <v-row>
              <v-col cols="12" sm="12" md="4">
                <v-card class="overflow-hidden" color="primary">
                  <font-awesome-icon
                    class="accent-icon white--text"
                    icon="envelope"
                  ></font-awesome-icon>
                  <v-card-text class="text-right white--text">
                    <div class="text-h3 text-md-h4 text-lg-h3">
                      {{ invitees | number }}
                    </div>
                    <div class="text-h6 text-md-subtitle-1 text-md-h6">
                      Invitees
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="12" md="4">
                <v-card class="overflow-hidden" color="secondary darken-2">
                  <font-awesome-icon
                    style="left: -1rem;"
                    class="accent-icon white--text"
                    :icon="['far', 'star']"
                  ></font-awesome-icon>
                  <v-card-text class="text-right white--text">
                    <div class="text-h3 text-md-h4 text-lg-h3">
                      {{ responses | number }}
                    </div>
                    <div class="text-h6 text-md-subtitle-1 text-md-h6">
                      Respondents
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="12" md="4">
                <v-card class="overflow-hidden" color="accent darken-2">
                  <font-awesome-icon
                    style="transform: rotate(0); left: 1rem;"
                    class="accent-icon white--text"
                    icon="chart-line"
                  ></font-awesome-icon>
                  <v-card-text class="text-right white--text">
                    <div class="text-h3 text-md-h4 text-lg-h3">
                      {{ (responses / invitees) | percentage }}
                    </div>
                    <div class="text-h6 text-md-subtitle-1 text-md-h6">
                      Response Rate
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card>
                  <v-card-title
                    class="d-flex align-center flex-md-row flex-column"
                  >
                    <div class="d-flex flex-fill align-center w-100">
                      <span class="text-h5 text-md-h4 flex-fill text-truncate"
                        >District &amp; School Response Rates</span
                      >

                      <v-btn
                        @click.stop="displayMode = 0"
                        elevation="0"
                        :outlined="displayMode != 0"
                        :color="
                          `grey ${displayMode == 0 ? 'lighten-2' : 'darken-2'}`
                        "
                        class="mr-2"
                      >
                        <font-awesome-icon
                          icon="table"
                          class="mr-2"
                        ></font-awesome-icon>
                        Grid
                      </v-btn>
                      <v-btn
                        @click="displayMode = 1"
                        elevation="0"
                        :outlined="displayMode != 1"
                        :color="
                          `grey ${displayMode == 1 ? 'lighten-2' : 'darken-2'}`
                        "
                      >
                        <font-awesome-icon
                          icon="bars"
                          class="mr-2"
                        ></font-awesome-icon>
                        List
                      </v-btn>
                    </div>

                    <v-text-field
                      class="mt-3 w-100"
                      @input="debounceSearch"
                      type="search"
                      placeholder="Search Districts and Schools"
                      filled
                      rounded
                      dense
                      hide-details
                      prepend-inner-icon="mdi-magnify"
                    ></v-text-field>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-alert color="secondary lighten-3 mb-0" border="left">
                      <div class="d-flex align-top">
                        <v-icon class="mr-2 ml-2">$info</v-icon>
                        <div>
                          Results will automatically refresh in
                          <strong>{{ timeTillNextPoll }} minutes.</strong>
                          <div class="text-caption">
                            Note: It could take up to 30min for a response to
                            appear in this report.
                          </div>
                        </div>
                      </div>
                    </v-alert>
                  </v-card-text>
                  <grid
                    v-show="displayMode === 0"
                    :items="flattenedResponseRates"
                    :search="search"
                  ></grid>
                  <list
                    v-show="displayMode === 1"
                    :items="responseRates"
                    :search="search"
                  ></list>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-fade-transition>
    </v-main>

    <v-dialog v-model="notReady" persistent width="300">
      <v-card color="white" dark>
        <v-card-text class="pt-6">
          <img
            alt=""
            aria-hidden="true"
            class="shrink mr-2 d-block m-auto"
            contain
            src="@/assets/nctwcs/logo.png"
            transition="scale-transition"
            width="100%"
          />
          <v-alert
            v-if="errorMessage"
            class="mt-8 mb-0"
            color="red"
            type="error"
            border="left"
            transition="scale-transition"
            >{{ errorMessage }}
          </v-alert>
          <div v-if="!errorMessage">
            <span class="sr-only">Loading</span>
            <v-progress-linear
              indeterminate
              color="primary"
              height="6"
              class="mb-0 mt-8"
            ></v-progress-linear>
          </div>
        </v-card-text>
        <v-card-text v-if="errorMessage">
          <v-btn block outlined color="primary" @click="refresh"
            >Try again</v-btn
          >
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import numeral from "numeral";
import debounce from "debounce";
import { padEntityId } from "../../../store/entities/actions";

import surveyService from "../../../services/surveyService";
import Grid from "./Results.ResponseRate.Grid.vue";
import List from "./Results.ResponseRate.List.vue";

const tenantId = "NC_DPI";

const pollRate = 60 * 10000; //1000;

export default {
  name: "LiveResults",
  components: {
    Grid,
    List
  },
  head: {
    title: {
      inner: "North Carolina Teacher Working Conditions Survey",
      complement: " ",
      separator: " "
    }
  },
  filters: {
    percentage(val) {
      return numeral(val).format("0.00%");
    }
  },
  data: () => ({
    pendingStateChanges: 0,
    notReady: true,
    pageYOffset: 0,
    shrinkThreshold: 70,
    errorMessage: null,
    survey: {},
    codeStats: [],
    schools: [],
    districts: [],
    responseRates: [],
    flattenedResponseRates: [],
    polling: null,
    ticker: null,
    timeSinceLastPolled: 0,
    // Control States
    displayMode: 0,
    search: ""
  }),
  computed: {
    loading() {
      return this.pendingStateChanges > 0;
    },
    appbarHeight() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 148
        : 70;
    },
    logoWidth() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 128
        : 48;
    },
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    title() {
      return this.survey.title;
    },
    surveyId() {
      return this.$route.params.id;
    },
    entities() {
      return this.$store.state.entities.data.slice().map(x => {
        if (Array.isArray(x.children)) {
          x.children = x.children.map(y => {
            y.id = padEntityId(y.id);
            return y;
          });
        }
        return x;
      });
    },
    invitees() {
      return this.codeStats.reduce((agg, x) => agg + x.total, 0);
    },
    responses() {
      return this.codeStats.reduce((agg, x) => agg + x.used, 0);
    },
    timeTillNextPoll() {
      let result = Math.ceil((pollRate / 1000 - this.timeSinceLastPolled)/60);
      return result > 0 ? result - 1 : 0;
    }
  },
  watch: {
    errorMessage(val, prev) {
      if (val != prev && !this.notReady) {
        this.notReady = true;
      }
    },
    surveyId(val, prev) {
      if (val != prev) {
        this.loadData();
      }
    },
    entities() {
      this.setResponseRates();
    },
    responseRates() {
      this.populate(this.responseRates, 0);
    }
  },
  methods: {
    handleScroll() {
      this.pageYOffset = window.pageYOffset;
    },
    refresh() {
      window.location.reload();
    },
    pollData() {
      this.ticker = setInterval(() => {
        this.timeSinceLastPolled++;
      }, 1000);
      this.polling = setInterval(() => {
        this.timeSinceLastPolled = 0;
        this.loadStats();
      }, pollRate);
    },
    loadStats() {
      this.pendingStateChanges++;
      surveyService
        .getCodeStats(tenantId, this.surveyId)
        .then(
          response => {
            this.codeStats = response.codeStats.map(x => {
              let i = x.metaData.findIndex(y => y.key === "EntityId");
              if (i > -1)
                x.metaData[i].value = padEntityId(x.metaData[i].value);
              return x;
            });
            this.setResponseRates();
            this.populate(this.responseRates, 0);
          },
          error => {
            this.errorMessage =
              error.errorMessage ?? "An unexpected error occurred.";
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    loadData() {
      // Artificial delay to eliminate state stutter
      let minWait = Date.now() + 1000;

      this.notReady = true;
      this.aggregates = [];
      this.codeStats = null;

      let queue = [];

      queue.push(surveyService.get(tenantId, this.surveyId, true));
      queue.push(surveyService.getCodeStats(tenantId, this.surveyId));
      queue.push(this.$store.dispatch("entities/load"));

      return Promise.all(queue).then(
        results => {
          let next = () => {
            if (minWait < Date.now()) {
              let startDate = new Date(results[0].startDate);
              let now = new Date();

              if (now < startDate) {
                this.errorMessage = "The survey hasn't started.";
                return;
              }

              this.notReady = false;
              this.survey = results[0];
              this.codeStats = results[1].codeStats;
              this.setResponseRates();
              this.populate(this.responseRates, 0);
              this.pollData();
            } else setTimeout(next, 100);
          };

          next();
        },
        error => {
          this.errorMessage =
            error.errorMessage ?? "An unexpected error occurred.";
        }
      );
    },
    setResponseRates() {
      let getRates = entities => {
        var results = [];

        for (let i = 0; i < entities.length; i++) {
          let result = {};
          if (entities[i].children?.length) {
            result.children = getRates(entities[i].children);
            result.id = entities[i].id;
            result.name = entities[i].name;
            result.invitees = result.children.reduce(
              (agg, x) => agg + x.invitees,
              0
            );
            result.responses = result.children.reduce(
              (agg, x) => agg + x.responses,
              0
            );
          } else {
            let stat = this.codeStats?.find(x => {
              var entityId = x.metaData.find(y => y.key === "EntityId")?.value;

              return entities[i].id === entityId;
            });

            if (stat) {
              result.id = entities[i].id;
              result.name = entities[i].name;
              result.invitees = stat.total;
              result.responses = stat.used;
            }
          }

          if (result.id) results.push(result);
        }

        return results;
      };

      this.responseRates = getRates(this.entities);

      this.flattenedResponseRates = [].concat.apply(
        [],
        this.responseRates.slice().map(x =>
          x.children
            ? x.children.map(y => {
                y.districtId = x.id;
                y.districtName = x.name;
                y.responseRate = numeral(y.responses / y.invitees).format(
                  "0.00%"
                );
                return y;
              })
            : [
                {
                  districtId: x.id,
                  districtName: x.name,
                  ...x,
                  responseRate: numeral(x.responses / x.invitees).format(
                    "0.00%"
                  )
                }
              ]
        )
      );
    },
    populate(arr) {
      for (let i = 0; i < arr.length; i++) {
        if (!arr[i].children) {
          this.schools.push(arr[i]);
        }

        if (arr[i].children) {
          this.districts.push(arr[i]);
          this.populate(arr[i].children);
        }
      }
    },
    debounceSearch: debounce(function(e) {
      this.search = e;
    }, 250)
  },
  created() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    clearInterval(this.polling);
    clearInterval(this.ticker);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  mounted() {
    this.pageYOffset = window.pageYOffset;

    let favicon = document.querySelector("#favicon");
    favicon.href = require("../../../assets/nctwcs/logo.png");

    // Configure App
    this.$store.commit("setTheme", "nctwcs");
    this.$store.commit("setTenantId", "NC_DPI");

    this.loadData();
  }
};
</script>

<style lang="scss" scoped>
html {
  font-size: 1rem;
}

#mainNav {
  &,
  .v-toolbar__content,
  img {
    transition: all 200ms ease-in-out !important;
  }
}

.v-icon.v-icon.overlay-back {
  font-size: 24px !important;
}

.accent-icon {
  font-size: 7rem;
  position: absolute;
  top: 0rem;
  left: 0.5rem;
  transform: rotate(-20deg);
  opacity: 0.35;
}

.rotate-180 {
  transform: rotate(180deg);
}

.willTransform {
  transition: transform 250ms ease-in-out;
}

.scroller {
  overflow: auto;
  margin-bottom: 50px;
  height: 600px;
}
</style>
